import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../config';
import styles from './ContactPage.module.css';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch(`${getApiUrl('/api/contact')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: result.message,
        });
        // Reset form on success
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send message. Please try again.',
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.contactPage}>
      <div className={styles.contactContainer}>
        {/* Header */}
        <div className={styles.contactHeader}>
          <div className={styles.contactIcon}>
            <svg
              className={styles.contactIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              style={{ maxWidth: '32px', maxHeight: '32px' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h1 className={styles.contactTitle}>Contact Us</h1>
          <p className={styles.contactSubtitle}>
            We&apos;re here to help! Get in touch with our customer support team
            and we&apos;ll get back to you as soon as possible.
          </p>
        </div>

        {/* Contact Information Cards */}
        <div className={styles.contactCards}>
          {/* Email Support */}
          <div className={styles.contactCard}>
            <div className={styles.contactCardIcon}>
              <svg
                className={styles.contactCardIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                style={{ maxWidth: '24px', maxHeight: '24px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className={styles.contactCardTitle}>Email Support</h3>
            <p className={styles.contactCardText}>
              Send us an email and we&apos;ll get back to you within 24 hours.
            </p>
            <a
              href="mailto:tybierwagen@gmail.com"
              className={styles.contactCardButton}
            >
              <svg
                className={styles.contactCardButtonIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                style={{ maxWidth: '16px', maxHeight: '16px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              tybierwagen@gmail.com
            </a>
          </div>

          {/* Phone Support */}
          <div className={styles.contactCard}>
            <div className={styles.contactCardIcon}>
              <svg
                className={styles.contactCardIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                style={{ maxWidth: '24px', maxHeight: '24px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
            <h3 className={styles.contactCardTitle}>Phone Support</h3>
            <p className={styles.contactCardText}>
              Call us Monday through Friday, 9 AM - 6 PM EST.
            </p>
            <a href="tel:+1-972-679-6262" className={styles.contactCardButton}>
              <svg
                className={styles.contactCardButtonIcon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                style={{ maxWidth: '16px', maxHeight: '16px' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              +1 (972)-679-6262
            </a>
          </div>
        </div>

        {/* Business Hours */}
        <div className={styles.businessHours}>
          <div className={styles.businessHoursTitle}>
            <h3>Business Hours</h3>
            <div className={styles.businessHoursDivider}></div>
          </div>
          <div className={styles.businessHoursGrid}>
            <div className={styles.businessHoursCard}>
              <h4 className={styles.businessHoursCardTitle}>
                <svg
                  className={`${styles.businessHoursIcon} ${styles.blue}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Customer Support
              </h4>
              <div className={styles.businessHoursList}>
                <div className={styles.businessHoursItem}>
                  <span>Monday - Friday:</span>
                  <span>9:00 AM - 6:00 PM EST</span>
                </div>
                <div className={styles.businessHoursItem}>
                  <span>Saturday:</span>
                  <span>10:00 AM - 4:00 PM EST</span>
                </div>
                <div className={styles.businessHoursItem}>
                  <span>Sunday:</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
            <div className={styles.businessHoursCard}>
              <h4 className={styles.businessHoursCardTitle}>
                <svg
                  className={`${styles.businessHoursIcon} ${styles.green}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                Order Processing
              </h4>
              <div className={styles.businessHoursList}>
                <div className={styles.businessHoursItem}>
                  <span>Monday - Friday:</span>
                  <span>8:00 AM - 5:00 PM EST</span>
                </div>
                <div className={styles.businessHoursItem}>
                  <span>Saturday:</span>
                  <span>9:00 AM - 3:00 PM EST</span>
                </div>
                <div className={styles.businessHoursItem}>
                  <span>Sunday:</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className={styles.faqSection}>
          <div className={styles.faqTitle}>
            <h3>Frequently Asked Questions</h3>
            <div className={styles.faqDivider}></div>
          </div>
          <div className={styles.faqList}>
            <div className={styles.faqItem}>
              <h4 className={styles.faqItemTitle}>
                <svg
                  className={`${styles.faqIcon} ${styles.purple}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                How long does shipping take?
              </h4>
              <p className={styles.faqItemText}>
                All products are made to order after the website closes.
                Production takes 2-3 weeks, followed by 3-7 business days for
                shipping. Total timeline is approximately 3-4 weeks from website
                closing to delivery.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h4 className={styles.faqItemTitle}>
                <svg
                  className={`${styles.faqIcon} ${styles.blue}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                What is your return policy?
              </h4>
              <p className={styles.faqItemText}>
                All sales are final. We do not offer refunds, returns, or
                exchanges under any circumstances. This policy applies to all
                products and all orders, without exception.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h4 className={styles.faqItemTitle}>
                <svg
                  className={`${styles.faqIcon} ${styles.green}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Do you ship internationally?
              </h4>
              <p className={styles.faqItemText}>
                Currently, we only ship within the United States. We&apos;re
                working on expanding our shipping options.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h4 className={styles.faqItemTitle}>
                <svg
                  className={`${styles.faqIcon} ${styles.orange}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
                How can I track my order?
              </h4>
              <p className={styles.faqItemText}>
                You&apos;ll receive a tracking number via email once your order
                ships after production is complete.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h4 className={styles.faqItemTitle}>
                <svg
                  className={`${styles.faqIcon} ${styles.green}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ maxWidth: '20px', maxHeight: '20px' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                Why are products made to order?
              </h4>
              <p className={styles.faqItemText}>
                We make all products to order to minimize waste and ensure the
                highest quality. Production begins only after the website
                closes, allowing us to create intentionally and sustainably
                while maintaining craftsmanship standards.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className={styles.contactContent}>
          <div className={styles.contactSection}>
            <h3 className={styles.contactSectionTitle}>Send us a Message</h3>
          </div>

          {/* Status Messages */}
          {submitStatus.type && (
            <div
              className={`${styles.statusMessage} ${
                submitStatus.type === 'success' ? styles.success : styles.error
              }`}
            >
              <div className={styles.statusMessageContent}>
                {submitStatus.type === 'success' ? (
                  <svg
                    className={styles.statusMessageIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className={styles.statusMessageIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                <span className={styles.statusMessageText}>
                  {submitStatus.message}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.contactForm}>
            <div className={styles.formGroup}>
              <label htmlFor="firstName" className={styles.formLabel}>
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className={styles.formInput}
                placeholder="Enter your first name"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="lastName" className={styles.formLabel}>
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className={styles.formInput}
                placeholder="Enter your last name"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.formLabel}>
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className={styles.formInput}
                placeholder="Enter your email address"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="subject" className={styles.formLabel}>
                Subject *
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className={styles.formSelect}
              >
                <option value="">Select a subject</option>
                <option value="order-inquiry">Order Inquiry</option>
                <option value="shipping-question">Shipping Question</option>
                <option value="return-request">Return Request</option>
                <option value="product-question">Product Question</option>
                <option value="general-inquiry">General Inquiry</option>
                <option value="complaint">Complaint</option>
                <option value="compliment">Compliment</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.formLabel}>
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleInputChange}
                required
                className={styles.formTextarea}
                placeholder="Please describe your inquiry in detail..."
              ></textarea>
            </div>
            <div className={styles.formSubmitContainer}>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`${styles.submitButton} ${
                  isSubmitting ? styles.disabled : ''
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      style={{ maxWidth: '20px', maxHeight: '20px' }}
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Sending...</span>
                  </div>
                ) : (
                  <span>Send Message</span>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Back to Shopping */}
        <div className={styles.contactBackButton}>
          <Link to="/" className={styles.backToShoppingButton}>
            <svg
              className={styles.backToShoppingIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>Back to Shopping</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
